'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useState, type ReactNode, type SVGProps } from 'react';
import { useRouter } from 'next/navigation';
import { api } from '@/data/api';
import type { User } from '@/data/types/user';
import { popup } from '@/components/popup/popup';
import { UserAvatar } from '@/components/user/user-avatar';
import { hasAdminAccess } from '@/data/account-types';
export function UserMenu({
  user,
  tripsHref = '/trips',
  newTripHref = '/trips/new',
  newGroupHref = '/groups/new',
  settingsHref = '/settings',
  onCreateTrip,
  onCreateGroup
}: {
  user: User;
  tripsHref?: string;
  newTripHref?: string;
  newGroupHref?: string;
  settingsHref?: string;
  onCreateTrip?: () => void;
  onCreateGroup?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const router = useRouter();
  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError(false);
    try {
      const response = await api.organization.user.authentication.logout.fetch();
      if (!response?.loggedOut) {
        setLogoutError(true);
        return;
      }
      router.replace('/');
      router.refresh();
    } catch {
      setLogoutError(true);
    } finally {
      setLoggingOut(false);
    }
  };
  return <div className="relative">
            <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(value => !value)} className="flex min-w-0 items-center gap-2.5 rounded-full border border-border bg-surface/70 p-1.5 pr-2.5 text-left backdrop-blur-xl transition hover:bg-surface-hover sm:pr-3">
                <UserAvatar user={user} size={32} />
                <span className="hidden max-w-40 truncate text-sm font-bold text-text-primary sm:block">{user.displayName}</span>
                <ChevronIcon className={`hidden size-4 text-text-muted transition sm:block ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && <popup.div onClose={() => setOpen(false)} distance={10} horizontal="auto" vertical="bottom" priority="vertical" role="menu" className="w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border bg-surface-overlay p-2 text-text-primary shadow-float">
                    <div className="flex items-center gap-3 rounded-xl bg-brand-primary-soft p-3">
                        <UserAvatar user={user} size={46} />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-black text-text-primary">{user.displayName}</p>
                            <p className="truncate text-xs text-text-muted">{user.email}</p>
                            {user.handle && <p className="mt-0.5 truncate text-[11px] font-semibold text-brand-primary">@{user.handle}</p>}
                            {user.accountType && user.accountType !== 'user' && <span className="mt-1.5 inline-flex rounded-full bg-surface/70 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] text-brand-primary">{user.accountType}</span>}
                        </div>
                    </div>

                    <div className="mt-2 grid gap-1">
                        <MenuLink href={tripsHref} icon={<TripsIcon />} onClick={() => setOpen(false)} label="View trips" description="Open your trips" />
                        {onCreateTrip ? <MenuAction icon={<PlusIcon />} onClick={() => {
          setOpen(false);
          onCreateTrip();
        }} label="Create trip" description="Start planning a new trip" /> : <MenuLink href={newTripHref} icon={<PlusIcon />} onClick={() => setOpen(false)} label="Create trip" description="Start planning a new trip" />}
                        {onCreateGroup ? <MenuAction icon={<GroupIcon />} onClick={() => {
          setOpen(false);
          onCreateGroup();
        }} label="Create group" description="Build a new crew" /> : <MenuLink href={newGroupHref} icon={<GroupIcon />} onClick={() => setOpen(false)} label="Create group" description="Build a new crew" />}
                    </div>

                    <div className="my-2 h-px bg-border" />

                    <div className="grid gap-1">
                        {hasAdminAccess(user.accountType) && <MenuLink href="/admin" icon={<AdminIcon />} onClick={() => setOpen(false)} label="Admin Menu" description="Analytics and account controls" />}
                        <MenuLink href={settingsHref} icon={<SettingsIcon />} onClick={() => setOpen(false)} label="Settings" description="Manage your account" />
                    </div>

                    <div className="my-2 h-px bg-border" />

                    {logoutError && <p role="alert" className="mx-2 mb-1 rounded-lg bg-error-soft px-3 py-2 text-xs text-error-text">Unable to log out. Try again.</p>}
                    <button type="button" role="menuitem" disabled={loggingOut} onClick={() => void logout()} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-error-text transition hover:bg-error-soft disabled:cursor-wait disabled:opacity-50">
                        <LogoutIcon className="size-5 shrink-0" />
                        <span className="text-sm font-bold">{loggingOut ? 'Logging out…' : 'Log out'}</span>
                    </button>
                </popup.div>}
        </div>;
}
function MenuAction(t0) {
  const $ = _c(13);
  const {
    icon,
    label,
    description,
    onClick
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-surface-sunken text-brand-primary transition group-hover:bg-brand-primary-soft">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="block text-sm font-bold text-text-primary">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== description) {
    t3 = <span className="block truncate text-xs text-text-muted">{description}</span>;
    $[4] = description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="min-w-0">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onClick || $[10] !== t1 || $[11] !== t4) {
    t5 = <button type="button" role="menuitem" onClick={onClick} className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-surface-hover">{t1}{t4}</button>;
    $[9] = onClick;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function MenuLink(t0) {
  const $ = _c(14);
  const {
    href,
    icon,
    label,
    description,
    onClick
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-surface-sunken text-brand-primary transition group-hover:bg-brand-primary-soft">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="block text-sm font-bold text-text-primary">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== description) {
    t3 = <span className="block truncate text-xs text-text-muted">{description}</span>;
    $[4] = description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="min-w-0">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== href || $[10] !== onClick || $[11] !== t1 || $[12] !== t4) {
    t5 = <Link href={href} role="menuitem" onClick={onClick} className="group flex items-center gap-3 rounded-xl px-3 py-2.5 no-underline transition hover:bg-surface-hover">{t1}{t4}</Link>;
    $[9] = href;
    $[10] = onClick;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function ChevronIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="m6 9 6 6 6-6" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function TripsIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M3 7h18M5 7l1.5-3h11L19 7M5 7v12h14V7M9 11h6" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function PlusIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M12 5v14M5 12h14" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function GroupIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function AdminIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M4 19V9m5 10V5m5 14v-7m5 7V3" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function SettingsIcon(props) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <circle cx="12" cy="12" r="3" />;
    t1 = <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.1A1.7 1.7 0 0 0 8.5 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.1 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H2v-4h.4A1.7 1.7 0 0 0 4.1 8.5a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 8.5 4.1a1.7 1.7 0 0 0 1-.6A1.7 1.7 0 0 0 9.9 2H14a1.7 1.7 0 0 0 .4 1.5 1.7 1.7 0 0 0 1 .6 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.9 8.5a1.7 1.7 0 0 0 .6 1 1.7 1.7 0 0 0 1.1.4H22V14h-.4a1.7 1.7 0 0 0-1.1.4 1.7 1.7 0 0 0-1.1.6Z" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5" {...props}>{t0}{t1}</svg>;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function LogoutIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M10 17l5-5-5-5M15 12H3M21 19V5a2 2 0 0 0-2-2h-5" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}